// pages/monitor/panels/peak/data.js —— 削峰填谷面板局部演示数据与派生曲线
// 【演示数据，待接口接入】仅用于填充《云平台单站控制（削峰填谷）需求文档》要求的字段，
// 公共口径数据一律取自 src/config/stationData.js，本文件只补充数据层缺失的削峰填谷专有项。
import { getPowerCurve, strategyPowerAtMin } from '../../../../config/stationData';

const pad = (n) => String(n).padStart(2, '0');
const dayN = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// ============================== 实时运行 · 1 分钟颗粒度曲线 ==============================
// 文档 p2：今日 0 点~次日 6 点 · 1 分钟；功率零阶保持，SOC 可插值；策略功率仅「当前~次日 06:00」
const minuteCache = {};
export const getMinuteRealtime = (station, now = new Date()) => {
  if (minuteCache[station.id]) return minuteCache[station.id];
  const base = getPowerCurve(station.id);
  const hold = (key, m) => base[Math.min(Math.floor(m / 15), base.length - 1)][key];
  const lerpSoc = (m) => {
    const t = m / 15;
    const i = Math.floor(t);
    const a = base[Math.min(i, base.length - 1)];
    const b = base[Math.min(i + 1, base.length - 1)];
    return +(a.soc + (b.soc - a.soc) * (t - i)).toFixed(2);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const rows = Array.from({ length: 30 * 60 }, (_, m) => ({
    m,
    clock: m >= 1440
      ? `次日${pad(Math.floor((m - 1440) / 60))}:${pad((m - 1440) % 60)}`
      : `${pad(Math.floor(m / 60))}:${pad(m % 60)}`,
    grid: hold('grid', m),
    load: hold('load', m),
    storage: hold('storage', m),
    pv: hold('pv', m),
    soc: lerpSoc(m),
    strategy: m >= nowMin ? strategyPowerAtMin(m % 1440) : null,
  }));
  minuteCache[station.id] = rows;
  return rows;
};

// ============================== 收益 / 电量报表（按分时时段） ==============================
// 文档 p14 / p15：充电成本 / 放电收益按尖、峰、平、谷、深谷分时段计算；实际收益 = 放电收益 - 充电成本。
export const TARIFF_SEGMENTS = [
  { key: 'sharp', label: '尖', price: 1.28 },
  { key: 'peak', label: '峰', price: 1.05 },
  { key: 'flat', label: '平', price: 0.65 },
  { key: 'valley', label: '谷', price: 0.32 },
  { key: 'deep', label: '深谷', price: 0.22 },
];

// 演示：各时段充电 / 放电电量（kWh）
const segEnergy = (i, seg, dir) => {
  const seed = (i + 1) * (dir === 'c' ? 13 : 17) + seg * 7;
  return Math.round((dir === 'c' ? 120 : 150) + (seed % 60) + i * (dir === 'c' ? 6 : 9));
};

export const getIncomeRows = () =>
  Array.from({ length: 7 }, (_, i) => {
    const day = dayN(i + 1);
    const row = { key: day, day };
    let chargeCost = 0;
    let dischargeIncome = 0;
    TARIFF_SEGMENTS.forEach((s, idx) => {
      const c = segEnergy(i, idx, 'c');
      const d = segEnergy(i, idx, 'd');
      row[`c${s.key}`] = +(c * s.price).toFixed(1);
      row[`d${s.key}`] = +(d * s.price).toFixed(1);
      chargeCost += c * s.price;
      dischargeIncome += d * s.price;
    });
    row.chargeCost = +chargeCost.toFixed(1);
    row.dischargeIncome = +dischargeIncome.toFixed(1);
    row.actual = +(dischargeIncome - chargeCost).toFixed(1);
    return row;
  });

export const getEnergyRows = () =>
  Array.from({ length: 7 }, (_, i) => {
    const day = dayN(i + 1);
    const row = { key: day, day };
    let charge = 0;
    let discharge = 0;
    TARIFF_SEGMENTS.forEach((s, idx) => {
      const c = segEnergy(i, idx, 'c');
      const d = segEnergy(i, idx, 'd');
      row[`charge_${s.key}`] = c;
      row[`discharge_${s.key}`] = d;
      charge += c;
      discharge += d;
    });
    row.charge = charge;
    row.discharge = discharge;
    row.efficiency = +((discharge / charge) * 100).toFixed(1); // 综合效率 = 放电量 / 充电量 × 100%
    return row;
  });

// ============================== 电表报表 ==============================
// 文档 p17：电表电量默认查询近 7 天（不含今日），列分时电量与来源（电表分时 / EMS 分时）；
// 文档 p18：电表止度默认查询近 15 天（不含今日），展示 00:00 止度与设置的综合倍率。
export const METER_NAMES = ['并网柜电表 M-01', '储能进线电表 M-02', '负荷出线电表 M-03'];
const METER_RATIO = [1, 1, 40]; // 综合倍率

export const getMeterEnergyRows = () =>
  Array.from({ length: 7 }, (_, i) => METER_NAMES.map((m, j) => ({
    key: `${dayN(i + 1)}-${j}`,
    day: dayN(i + 1),
    meter: m,
    sharp: 108 + i * 3 + j * 5,
    peak: 196 + i * 4 + j * 3,
    flat: 168 + i * 2,
    valley: 92 + i,
    deep: 38 + i,
    source: (i + j) % 2 === 0 ? '电表分时' : 'EMS分时',
    ratio: METER_RATIO[j],
  }))).flat();

export const getMeterReadingRows = () =>
  Array.from({ length: 15 }, (_, i) => {
    const n = i + 1;
    return METER_NAMES.map((m, j) => ({
      key: `${dayN(n)}-${j}`,
      day: dayN(n),
      meter: m,
      reading: +((j + 1) * 860.4 - n * 2.3 + j * 120).toFixed(1), // 00:00 止度（已乘综合倍率的实际电度）
      ratio: METER_RATIO[j],
      time: `${dayN(n)} 00:00`,
    }));
  }).flat();

// ============================== 电池簇 / 单体曲线 ==============================
// 文档 p20：电池簇曲线支持对比（近 2 天）/ 趋势（近 7 天）；单体曲线为某单体最近 7 天的温度、电压、SOC。
const spanLabels = (days) => Array.from({ length: days }, (_, i) => `${dayN(days - i)}`);

export const getClusterCurveSeries = (span) => {
  const days = span === 'compare' ? 2 : 7;
  const labels = span === 'compare'
    ? Array.from({ length: 24 }, (_, h) => `${h % 12 === 0 ? 0 : Math.floor(h / 12)}日${pad(h % 24)}:00`)
    : spanLabels(days);
  return labels.map((clock, i) => ({
    clock,
    temp: +(29 + Math.sin(i / 2.4) * 3.2).toFixed(1),
    voltage: +(714 + Math.cos(i / 3.1) * 9).toFixed(1),
    current: +(168 + Math.sin(i / 1.7) * 34).toFixed(1),
    soc: +(72 + Math.sin(i / 2.2) * 11).toFixed(1),
  }));
};

export const getCellCurveSeries = (pick) =>
  spanLabels(7).map((clock, i) => ({
    clock,
    voltage: +(3.328 + Math.sin((i + pick) / 5.5) * 0.012).toFixed(3),
    temp: +(27.6 + Math.cos((i + pick) / 4.2) * 1.6).toFixed(1),
    soc: +(76 + Math.sin(i / 3) * 6).toFixed(1),
  }));

// 电池温度报表 / 电压报表：整点时列出该簇下所有电池，给出最高 / 最低单体及其 ID
export const getTempReportRows = () =>
  ['1 号簇', '2 号簇', '3 号簇', '4 号簇'].map((cluster, i) => ({
    key: cluster,
    cluster,
    time: `${dayN(1)} 12:00`,
    maxVal: +(31.4 - i * 0.5).toFixed(1),
    maxCell: `${(i + 1) * 26 - 4} 号`,
    minVal: +(23.8 + i * 0.4).toFixed(1),
    minCell: `${(i + 1) * 26 - 18} 号`,
  }));

export const getVoltReportRows = () =>
  ['1 号簇', '2 号簇', '3 号簇', '4 号簇'].map((cluster, i) => ({
    key: cluster,
    cluster,
    time: `${dayN(1)} 12:00`,
    maxVal: +(3.352 - i * 0.002).toFixed(3),
    maxCell: `${(i + 1) * 26 - 6} 号`,
    minVal: +(3.306 + i * 0.002).toFixed(3),
    minCell: `${(i + 1) * 26 - 20} 号`,
    diff: +((3.352 - i * 0.002) - (3.306 + i * 0.002)).toFixed(3),
  }));

// ============================== 自定义曲线 ==============================
// 文档 p22-24：最多选择 10 项数据；可保存 / 选择模板；可时段查询（到秒）或实时查询（今日 0 点至此刻）；
//             导出可选采样频率；全量单体导出最多支持 12h，且为异步导出（在「导出列表」查询结果）。
export const CUSTOM_METRICS = [
  { key: 'grid', label: '电网功率', unit: 'kW', color: '#4dd8ff' },
  { key: 'load', label: '负载功率', unit: 'kW', color: '#ff5a7f' },
  { key: 'storage', label: '储能功率', unit: 'kW', color: '#52e8b0' },
  { key: 'pv', label: '光伏功率', unit: 'kW', color: '#f4c542' },
  { key: 'soc', label: 'SOC', unit: '%', color: '#8b7cf6' },
  { key: 'strategy', label: '策略功率', unit: 'kW', color: '#00d4ff' },
];
export const CUSTOM_MAX = 10;
export const CUSTOM_EXPORT_LIMIT = '全量单体导出最多支持 12h（异步导出，请在「导出列表」查询结果）';
export const SAMPLE_RATES = ['1 分钟', '5 分钟', '15 分钟', '1 小时'];
export const CUSTOM_TEMPLATES = ['默认模板', '峰谷对比', '单体全量'];

// ============================== 消防安全（削峰填谷独有） ==============================
// 文档 p8-9：消防安全页面包含 IO 控制器（DI / DO 端口状态）、各类传感器、测控装置
// （参数由配置文件手动配置，点击「配置」可对测控点位进行单次写入）。
export const FIRE_IO = [
  {
    id: 'IO-01', name: 'IO 控制器 #1', state: '正常',
    di: [
      { key: 'DI1', name: '消防主机火警', value: '断开' },
      { key: 'DI2', name: '水浸探测', value: '闭合' },
      { key: 'DI3', name: '急停按钮', value: '断开' },
    ],
    do: [
      { key: 'DO1', name: '声光报警', value: '断开' },
      { key: 'DO2', name: '排风联动', value: '闭合' },
    ],
  },
  {
    id: 'IO-02', name: 'IO 控制器 #2', state: '正常',
    di: [
      { key: 'DI1', name: '舱门开合', value: '闭合' },
      { key: 'DI2', name: '烟感联动', value: '断开' },
    ],
    do: [{ key: 'DO1', name: '消防切非', value: '断开' }],
  },
];

export const FIRE_SENSORS = [
  { id: 'TH-01', name: '温湿度计 #1', type: '温湿度', value: '24.6 ℃ / 48.2 %RH', state: '正常' },
  { id: 'WL-01', name: '水浸探测器 #1', type: '水浸', value: '无漏水', state: '正常' },
  { id: 'SM-01', name: '烟感探测器 #1', type: '烟感', value: '0.02 %obs/m', state: '正常' },
  { id: 'H2-01', name: '氢气探测器 #1', type: '气体', value: '0 ppm', state: '正常' },
];

export const FIRE_MEASURE = [
  {
    id: 'MC-01', name: '测控装置 #1', state: '正常',
    rows: [
      { name: '交流电压', value: '398.6 V' },
      { name: '交流电流', value: '212.4 A' },
      { name: '有功功率', value: '128.6 kW' },
    ],
  },
  {
    id: 'MC-02', name: '测控装置 #2', state: '正常',
    rows: [
      { name: '母线电压', value: '400.2 V' },
      { name: '频率', value: '50.01 Hz' },
    ],
  },
];

// ============================== DCDC / UPS（削峰填谷独有） ==============================
// 文档 DCDC：状态摘要（运行状态 / 告警状态）+ 高压侧参数 / 低压侧参数 / 累计·环境参数 三栏数据；
// 文档 UPS：输入电压 / 输入频率 / 环境温度 / 电池电压 / 输出电压 / 输出电流 / 负载百分比。
export const DCDC_DEVICES = [
  {
    id: 'DCDC-01', name: 'DCDC 变换器 #1', running: true, state: '正常', alarm: '正常',
    hvVoltage: 750, hvCurrent: 62, hvPower: 46, hvPosBus: 375.2, hvNegBus: -375.1, hvDiff: 0.3,
    lvVoltage: 400, lvCurrent: 115, lvPower: 46, lvPosBus: 200.1, lvNegBus: -199.9, lvDiff: 0.2,
    totalCharge: 35200, totalDischarge: 12800, radiatorTemp: 41, envTemp: 28,
  },
  {
    id: 'DCDC-02', name: 'DCDC 变换器 #2', running: false, state: '停机', alarm: '故障',
    hvVoltage: 750, hvCurrent: 0, hvPower: 0, hvPosBus: 0, hvNegBus: 0, hvDiff: 0,
    lvVoltage: 400, lvCurrent: 0, lvPower: 0, lvPosBus: 0, lvNegBus: 0, lvDiff: 0,
    totalCharge: 35200, totalDischarge: 12800, radiatorTemp: 33, envTemp: 26,
  },
];

// ============================== 预置策略模板（按月配置） ==============================
// 文档 p29-31：可依据分时电价设定不同时段 PCS 充放功率；部分地区年内不同月份分时电价不同，支持按月配置模板。
export const PRESET_MONTHS = ['1 月', '2 月', '3 月', '4 月', '5 月', '6 月', '7 月', '8 月', '9 月', '10 月', '11 月', '12 月'];

// ============================== 日志（操作 / 动环 / EMS 运行） ==============================
export const PEAK_LOGS = {
  operation: [
    { key: 1, time: '2026-10-16 16:20:08', type: '操作日志', user: 'admin', action: '下发预置策略模板', target: '全站 PCS', result: '成功' },
    { key: 2, time: '2026-10-16 09:12:41', type: '操作日志', user: 'ops01', action: 'PCS-02 关机', target: 'PCS-02', result: '成功' },
    { key: 3, time: '2026-10-15 20:04:15', type: '操作日志', user: 'admin', action: '修改需量保护参数', target: '总表', result: '成功' },
  ],
  env: [
    { key: 1, time: '2026-10-16 14:02:33', type: '动环日志', user: '系统', action: '空调 #3 通信中断', target: 'AC-03', result: '告警' },
    { key: 2, time: '2026-10-16 06:00:00', type: '动环日志', user: '系统', action: '液冷机组切换自动模式', target: 'LC-01', result: '成功' },
  ],
  ems: [
    { key: 1, time: '2026-10-16 08:00:00', type: 'EMS(运行)日志', user: '系统', action: '进入峰段放电策略', target: '预置策略', result: '成功' },
    { key: 2, time: '2026-10-16 00:00:00', type: 'EMS(运行)日志', user: '系统', action: '进入谷段充电策略', target: '预置策略', result: '成功' },
  ],
};
